import { useCallback, useEffect, type CSSProperties as ReactCSSProperties } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { clearOnsiteInterviews } from '~/actions/PermanentJobs';
import { Button, Center, CustomSpinner, Row, Text } from '~/commonComponents';
import { OverlappingJobPopup } from '~/commonComponents/popups/OverlappingJobPopup';
import { useCreateOnsiteInterview, useGetJobPostDetail } from '~/domain/PermanentJobs';
import { OverlappingJobError } from '~/domain/PermanentJobs/errors';
import { PostingType } from '~/enums/PostingType';
import { useAppSelector, usePreventBackNavigation } from '~/hooks';
import { Colors } from '~/themes/colors';
import { OnsiteInterviewPreviewer } from '../components';
import { TJobOverviewPageRoutesLocationState } from '../JobOverview';
import { TCreateOnsiteInterviewPageRoutes } from './Types';
import {
  fireInterviewRequestCancelClicked,
  fireInterviewRequestInviteSent,
  fireInterviewRequestPostInterviewsClicked,
} from '~/posthogs/events';

const Styles = {
  BtnCancel: {
    backgroundColor: Colors.secondary_500,
  } as ReactCSSProperties,
};

export const ReviewAndConfirmPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: jobPostingId } = useParams<TCreateOnsiteInterviewPageRoutes>();

  // Prevent browser back button navigation - redirect to postings list instead
  usePreventBackNavigation({ targetKickUrl: '/permanent-jobs/postings' });

  const { data, isLoading } = useGetJobPostDetail(jobPostingId);
  const { error, isError, isPending, isSuccess, mutate, reset } = useCreateOnsiteInterview();

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.onsiteInterviews[jobPostingId] ?? [];
  });

  useEffect(() => {
    if (isSuccess && jobPostingId) {
      dispatch(clearOnsiteInterviews(jobPostingId));
      navigate(`/permanent-jobs/job-overview/${jobPostingId}/onsite-interviews`, {
        replace: true,
        state: {
          fromOnsiteInterviewCreation: true,
        } satisfies TJobOverviewPageRoutesLocationState,
      });
    }
  }, [dispatch, isSuccess, jobPostingId, navigate]);

  const handleCancel = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    // Track the cancel clicked event
    fireInterviewRequestCancelClicked({
      postingId: jobPostingId,
    });

    dispatch(clearOnsiteInterviews(jobPostingId));
    navigate(`/permanent-jobs/job-overview/${jobPostingId}/onsite-interviews`, { replace: true });
  }, [dispatch, jobPostingId, navigate]);

  const handleSubmit = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    // Track the post interviews clicked event
    fireInterviewRequestPostInterviewsClicked({
      postingId: jobPostingId,
      interviewCount: interviews.length,
    });

    const totalCandidates = interviews.reduce((counter, curr) => {
      return counter + (curr.professionals?.length || 0);
    }, 0);

    mutate(
      {
        jobPostingId,
        officeId: data?.officeId,
        onsiteInterviews: interviews,
      },
      {
        onSuccess() {
          fireInterviewRequestInviteSent({
            postingId: jobPostingId,
            totalCandidatesSelected: totalCandidates,
            totalInterviews: interviews.length,
            totalInvitesSent: totalCandidates,
          });
        },
      },
    );
  }, [data?.officeId, interviews, jobPostingId, mutate]);

  const handleSubmitWithDuplicate = useCallback(() => {
    if (!jobPostingId) {
      return;
    }

    const totalCandidates = interviews.reduce((counter, curr) => {
      return counter + (curr.professionals?.length || 0);
    }, 0);

    mutate(
      {
        duplicate: true,
        jobPostingId,
        officeId: data?.officeId,
        onsiteInterviews: interviews,
      },
      {
        onSuccess() {
          fireInterviewRequestInviteSent({
            postingId: jobPostingId,
            totalCandidatesSelected: totalCandidates,
            totalInterviews: interviews.length,
            totalInvitesSent: totalCandidates,
          });
        },
      },
    );
  }, [data?.officeId, interviews, jobPostingId, mutate]);

  if (isLoading || !jobPostingId) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  return (
    <>
      <OnsiteInterviewPreviewer jobPostingId={jobPostingId} />
      <Text color={Colors.neutral_500} italic textAlign="center" marginTop={28}>
        Please Note: Onsite interviews will incur a separate fee once candidates are{' '}
        <Text bold color={Colors.neutral_500} italic>
          confirmed
        </Text>{' '}
        by your office.
      </Text>
      <Row alignItems="center" justifyContent="center" gap={16} marginTop={66}>
        <Button
          color="secondary"
          disabled={isPending}
          text="Cancel"
          style={Styles.BtnCancel}
          onClick={handleCancel}
        />
        <Button
          color="primary"
          text="Post Interviews"
          isLoading={isPending}
          onClick={handleSubmit}
        />
      </Row>

      <OverlappingJobPopup
        isOpen={isError && error && error instanceof OverlappingJobError}
        jobPostingType={PostingType.OnsiteInterview}
        onGoBack={reset}
        onConfirm={handleSubmitWithDuplicate}
      />
    </>
  );
};
